export const metricOptions = [
    { value: 'loadUptime', label: 'Load & Uptime' },
    { value: 'PM10', label: 'PM10' },
    { value: 'PM25', label: 'PM2.5' },
    { value: 'AMBIENT_TEMP', label: 'Temperature' },
    { value: 'AMBIENT_HUMID', label: 'Humidity' },
    { value: 'AMBIENT_PRESS', label: 'Pressure' },
    { value: 'JAS', label: 'JAS' },
    { value: 'DUST_HEATER_NTC', label: 'Dust Heater' },
    { value: 'NH3_raw', label: 'NH₃' },
    { value: 'H2S_raw', label: 'H₂S' },
    { value: 'ODO', label: 'ODO' },
    { value: 'rssi', label: 'RSSI' },
    { value: 'snr', label: 'SNR' },
    { value: 'wifi_signal', label: 'WIFI_SIGNAL' },
];

export const getOperatorColor = (operator: string): string => {
    switch (operator) {
        case 'Orange': return '#FF9800';
        case 'Play': return '#AB47BC';
        case 'Plus': return '#66BB6A';
        default: return '#9E9E9E';
    }
};

export const determineOperator = (iccid: string): string => {
    if (iccid.startsWith('894803')) return 'Orange';
    if (iccid.startsWith('894806')) return 'Play';
    if (iccid.startsWith('894801')) return 'Plus';
    return 'Unknown';
};

export const uptimeToMinutes = (uptimeString: string): number => {
    if (!uptimeString) return 0;
    if (uptimeString.includes(':')) {
        const [hours, minutes] = uptimeString.split(':').map(Number);
        return hours * 60 + minutes;
    }
    return Number(uptimeString);
};

export const formatLegendValue = (value: number | null): string => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    if (value > 1000) return value.toFixed(0);
    return value.toFixed(2);
};

// Mapowanie reagentów do kolorów
export const reagentColors = {
    'H2S': '#c33f28', // czerwony
    'NH3': '#2c0c4b', // ciemny fioletowy
    'NO2': '#8b5235', // brązowy
    'O3': '#F5DEB3',  // beżowy
    'SO2': '#910d0d', // ciemny czerwony
    'CO2': '#161616', // czarny
    'CO': '#057f50',  // zielony
    'NO': '#0093cb',  // niebieski
    'PID': '#ffffff', // biały
    'PM': '#808080',  // szary
    'TG00': '#87eedc', // jasny turkusowy
    'TG02': '#baff47', // jasny zielony
    'TG03': '#ffa12e', // pomarańczowy
    'TG12': '#f9f246'  // żółty
};

// Funkcja zwracająca kolor dla danego reagentu
export const getReagentColor = (reagent: string): string => {
    return reagentColors[reagent as keyof typeof reagentColors] || '#CCCCCC';
};

// Priorytety dla reagentów - określa kolejność przypisywania pozycji
// PM zawsze ma najwyższy priorytet i będzie na pozycji 0
export const reagentPriorities: { [key: string]: number } = {
    'PM': 0,   // Najwyższy priorytet - zawsze na pozycji 0
    'H2S': 1,
    'NH3': 2,
    'NO2': 3,
    'O3': 4,
    'SO2': 5,
    'CO2': 6,
    'CO': 7,
    'NO': 8,
    'PID': 9,
    'TGS': 10,
    'TG00': 11,
    'TG02': 12,
    'TG03': 13,
    'TG12': 14
};

// Stałe pozycje dla najpopularniejszych reagentów (dla zachowania zgodności)
const defaultPositions: { [key: string]: number } = {
    'PM': 0    // PM zawsze na pozycji 0 (lewy górny róg)
};

// Funkcja dynamicznie przydzielająca pozycje reagentom
export const getReagentPosition = (reagent: string, usedPositions: Set<number>): number => {
    // Jeśli reagent ma stałą pozycję, użyj jej
    if (defaultPositions[reagent] !== undefined) {
        return defaultPositions[reagent];
    }
    
    // W przeciwnym razie znajdź pierwszą wolną pozycję
    for (let i = 0; i < 9; i++) {
        if (!usedPositions.has(i)) {
            return i;
        }
    }
    
    // Jeśli wszystkie pozycje są zajęte, użyj pozycji 0 (nadpisz PM)
    return 0;
};

// Dla zachowania kompatybilności ze starym kodem, zachowujemy starą strukturę
// ale będzie ona używana tylko jako fallback
export const reagentPositions: { [key: string]: number } = {
    'PM': 0,    // pozycja (0,0) - lewy górny róg
    'H2S': 1,   // pozycja (0,1) 
    'NH3': 2,   // pozycja (0,2)
    'NO2': 3,   // pozycja (1,0)
    'O3': 4,    // pozycja (1,1) - środek
    'SO2': 5,   // pozycja (1,2)
    'CO2': 6,   // pozycja (2,0)
    'CO': 7,    // pozycja (2,1)
    'NO': 8,    // pozycja (2,2) - prawy dolny róg
    'PID': 0,   // używamy tej samej pozycji co PM (zostanie zmienione przez getReagentPosition)
    'TGS': 1,   // używamy tej samej pozycji co H2S (zostanie zmienione przez getReagentPosition)
    'TG00': 2,  // używamy pozycji 2 (zostanie zmienione przez getReagentPosition)
    'TG02': 3,  // używamy pozycji 3 (zostanie zmienione przez getReagentPosition)
    'TG03': 4,  // używamy pozycji 4 (zostanie zmienione przez getReagentPosition)
    'TG12': 5   // używamy pozycji 5 (zostanie zmienione przez getReagentPosition)
};